import React from 'react';
import { Link } from 'react-router-dom';
import HashtagDisplay from './HashtagDisplay';
import { usePartyColors } from '../contexts/PartyColorContext';

const ParliamentMemberCard = ({ member, speechData, onHashtagClick }) => {
  const { speechCount, lastActive, topHashtags } = speechData;

  // Generate initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  // Format last active date
  const formatLastActive = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const { getPartyColor } = usePartyColors();

  const initials = getInitials(member.first_name, member.last_name);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full">
      {/* Card Content - flex-1 to take available space */}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start space-x-4">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300 font-semibold text-lg">
              {initials}
            </div>
          </div>
          
          {/* Member Info */}
          <div className="flex-1 min-w-0">
            <Link
              to={`/profile/${encodeURIComponent(`${member.first_name} ${member.last_name}`)}`}
              className="block"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 truncate hover:text-primary dark:hover:text-primary transition-colors cursor-pointer">
                {member.first_name} {member.last_name}
              </h3>
            </Link>
            
            {/* Party and Role Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              {member.party && (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: getPartyColor(member.party) }}
                >
                  {member.party}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Member Description */}
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {member.first_name} {member.last_name} {member.party ? `er ${member.party === 'Socialdemokratiet' ? 'Socialdemokrat' : 'medlem af ' + member.party}` : 'er medlem af Folketinget'}.
          </p>
          <Link 
            to={`/profile/${encodeURIComponent(`${member.first_name} ${member.last_name}`)}`}
            className="text-sm text-primary hover:text-primary/80 dark:text-primary dark:hover:text-primary/80 font-medium mt-1 inline-block"
          >
            Read more
          </Link>
        </div>

        {/* Activity Stats */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-gray-900 dark:text-white font-medium">{speechCount}</span>
              <span className="text-gray-500 dark:text-gray-400">speeches</span>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-xs text-gray-500 dark:text-gray-400">Last Active</div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {formatLastActive(lastActive)}
            </div>
          </div>
        </div>

        {/* Top Topics - Always reserve space, but only show content if hashtags exist */}
        <div className="mt-4 flex-1">
          {topHashtags && topHashtags.length > 0 ? (
            <>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Top Topics</div>
              <HashtagDisplay 
                hashtags={topHashtags}
                maxVisible={3}
                onHashtagClick={onHashtagClick}
                className="flex-wrap"
              />
              {topHashtags.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 text-xs text-gray-500 dark:text-gray-400 mt-1">
                  +{topHashtags.length - 3} more
                </span>
              )}
            </>
          ) : (
            <div className="text-xs text-gray-400 dark:text-gray-500 italic">
              No topics available
            </div>
          )}
        </div>
      </div>

      {/* Card Footer with View Profile Button - Always at bottom */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 mt-auto">
        <Link
          to={`/profile/${encodeURIComponent(`${member.first_name} ${member.last_name}`)}`}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
        >
          View Profile
          <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default ParliamentMemberCard;
